import React, { useEffect, useMemo, useRef } from 'react'
import FullPageSpinner from './FullPageSpinner'
import { date2DDMMMMMYYYString } from '../lib/textutils'
import '../styles/from-editor/text-styles.scss'

const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'TR', 'TD', 'TH', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE'])

const isTaiat = (el) => el.classList && el.classList.contains('lex-taiat')

const hasVisibleNonTaiatContent = (el) => {
  if (isTaiat(el)) return false
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (/\S/.test(node.textContent)) return true
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (isTaiat(node) || node.tagName === 'BR') continue
      if (hasVisibleNonTaiatContent(node)) return true
    }
  }
  return false
}

const DocumentBody = ({ loading, doc }) => {

  const docBodyRef = useRef(null)

  // console.log('DOC', doc)
  const {
    html,
    // cleanHtml,
    // reactified,
    newtype,
    title,
    publish_nr,
    publish_year,
    publisher_name,
    publish_date,
    revisions,
    deleted,
    type_name,
    status_code,
    status_name,
    issue_year,
    issuers,
    revdata,
    domains
  } = doc || {};

  // republished acts keep the base name in `newtype` ("ORDIN"), the full one is in `type_name`
  const type = type_name || newtype;

  const processedHtml = useMemo(() => {
    if (!doc || !doc.html) {
      return ''
    }
    let processed = doc.html

    while (processed.includes('  ')) {
      processed = processed.replace(/ {2}/g, ' &nbsp;')
    }

    // Custom parsing needed for Lege 147/2004
    // If content has newlines but no <br>/<p> tags, treat as plain text needing conversion
    if (processed.includes('\n') && !/<(br|p)\b/i.test(processed)) {
      const isTableLine = (line) =>
        line.includes('¦') || /^\s*\+[-+\s]*$/.test(line.trimEnd());

      const padAsciiTable = (tableLines) => {
        const countPlus = (s) => (s.match(/\+/g) || []).length;
        const completeBorder = tableLines
          .filter(l => /^\+[-+]+[+¦]$/.test(l.trimEnd()))
          .reduce((best, l) => countPlus(l) > countPlus(best ?? '') ? l : best, null);
        if (!completeBorder) return tableLines.join('\n');

        const border = completeBorder.trimEnd();
        const colPositions = [];
        for (let i = 0; i < border.length; i++) {
          if (border[i] === '+') colPositions.push(i);
        }
        if (border[border.length - 1] === '¦') {
          colPositions.push(border.length - 1);
        }
        if (colPositions.length < 2) return tableLines.join('\n');

        const expectedCols = colPositions.length - 1;
        const endsWithBar = border[border.length - 1] === '¦';

        const paddedLines = tableLines.map(line => {
          const trimmed = line.trimEnd();
          if (/^\+[-+¦\s]*$/.test(trimmed)) {
            let row = '';
            for (let c = 0; c < colPositions.length - 1; c++) {
              row += '+' + '-'.repeat(colPositions[c + 1] - colPositions[c] - 1);
            }
            return row + (endsWithBar ? '+' : '+');
          }
          if (trimmed.includes('¦')) {
            const dataCells = trimmed.split('¦').slice(1, -1);
            if (dataCells.length === expectedCols) {
              let row = '';
              for (let c = 0; c < expectedCols; c++) {
                const width = colPositions[c + 1] - colPositions[c] - 1;
                row += '¦' + dataCells[c].padEnd(width);
              }
              return row + '¦';
            }
          }
          return trimmed;
        });

        return paddedLines.join('\n');
      };

      const lines = processed.split('\n');
      let result = '';
      let tableLines = [];

      for (const line of lines) {
        if (isTableLine(line)) {
          tableLines.push(line);
        } else {
          if (tableLines.length > 0) {
            result += '<pre class="lex-plain-table">' + padAsciiTable(tableLines) + '</pre>';
            tableLines = [];
          }
          result += line + '<br>';
        }
      }
      if (tableLines.length > 0) {
        result += '<pre class="lex-plain-table">' + padAsciiTable(tableLines) + '</pre>';
      }

      processed = result;
    }

    return processed.replace(/\t/g, "<span data-type='lex-custom-tab'>\t</span>");
  }, [doc])

  useEffect(() => {
    const root = docBodyRef.current
    if (!root) return
    const candidates = new Set()
    root.querySelectorAll('.lex-taiat').forEach((t) => {
      let p = t.parentElement
      while (p && p !== root) {
        if (BLOCK_TAGS.has(p.tagName)) {
          candidates.add(p)
          break
        }
        p = p.parentElement
      }
    })
    candidates.forEach((blk) => {
      blk.classList.toggle('lex-taiat-block', !hasVisibleNonTaiatContent(blk))
    })
  }, [doc])

  if (loading && !doc)
    return <div id="document-body"><FullPageSpinner spin={loading} /></div>
  if (!doc)
    return null

  const actionString = revdata.rev_type === 'Publicare' ? revdata.rev_number === 1 ? 'Publicare' : 'Republicare' : 'Modificare'
    , formattedRevDate = date2DDMMMMMYYYString(new Date(revdata.rev_date))
    , formattedPubDate = date2DDMMMMMYYYString(new Date(publish_date))
    , issuerTexts = issuers ? issuers.map(i => `${i.issuer_name} Nr. ${i.issuer_nr}/${issue_year}`) : ""
    , singleIssuerNumber = issuers.length === 1 ? ` Nr. ${issuers[0].issuer_nr}/${issue_year}` : ""
  let statusBadgeClass = 'light'
  switch (status_code) {
    case 'A':
    case 'I':
      statusBadgeClass = 'danger'
      break;
    case 'B':
    case 'L':
    case 'O':
    case 'v':
      statusBadgeClass = 'warning'
      break;
    case 'C':
    case 'M':
      statusBadgeClass = 'info'
      break;
    case 'V':
    case 'P':
    case 'R':
    case 'r':
      statusBadgeClass = 'success'
      break;
    default:
      statusBadgeClass = 'secondary'
  }
  document.title = `Lex - ${title.replace(/<\/?[^>]+>/g, '')} - ${actionString} ${formattedRevDate}`
  return (
    <div id="fulldoc">
      {deleted && <h2 className="hide4print" style={{ color: "red" }}>ACEST DOCUMENT ESTE MARCAT CA STERS IN BAZA DE DATE</h2>}
      <div className="lex-preview-content-header py-3 text-center hide4print">
        <div className="mb-2"><strong>{publisher_name} nr. {publish_nr}/{publish_year}</strong> din <strong>{formattedPubDate}</strong></div>
        <div className="font-weight-bold">{type} {singleIssuerNumber}<br />{issuerTexts.map((it, i) => <span className="badge badge-light" key={`issuer-${it}`}>{it}</span>)}</div>
      </div>
      <div className="lex-preview-content-header text-center hide4print">
        <div className="w-100 text-center mb-2">
          <h1 style={{ fontSize: 25 }} dangerouslySetInnerHTML={{ __html: title }}></h1>
        </div>
        <small>Stare: </small><span className={`badge badge-${statusBadgeClass}`}>{status_name}</span>
        <br />
        <small>Emitenti: <strong>{issuers.map(i => i.issuer_name).join(', ')}</strong></small>
        <br />
        <small>Domenii: <strong>{domains.map(d => d.domain_name).join(', ')}</strong></small>
        <br />
      </div>
      {revdata.rev_type === 'Modificare' ?
        (
          <div className="lex-preview-content-headerxx w-100 text-center mb-2 pb-2 font-italic">
            {revdata.rev_type === 'Modificare' && revdata.rev_date === revisions[revisions.length - 1].rev_date ? 'ACT COMPUS - FORMA APLICABILA AZI!' : ''}
            {/* Acesta este actul compus creat la data de <strong>{formattedRevDate}</strong> */}
          </div>
        )
        : null}
      <div className="lex-preview-content-text data-lex-act-content">
        {/*reactified*/}
        {<div id="document-body" ref={docBodyRef} dangerouslySetInnerHTML={{ __html: processedHtml }} />}
      </div>
      <div><FullPageSpinner spin={loading} /></div>
    </div>

  )
}

export default DocumentBody
